import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import axiosInstant from '../../../api';
import { MainContainer } from '../../../components/ContentWrapper';
import { DotLottleLoader } from '../../../components/Loader';
import { PageTitle } from '../../../components/PageTitle';
import { NoDataView } from '../../../components/EmptyPanel/NoDataView';
import { PageDivider } from '../../../components/Loader/PageDivider';
import { IconVideo } from '../../../utils/icons';
import ExpertProjectCard from './ExpertProjectCard';
import ExpertSowSignModal from '../../../components/ExpertSowSignModal';
import ContactReminderBanner from '../../../components/ContactReminderBanner/ContactReminderBanner';
import { useUser } from '../../../hooks/useUser';
import { getUserSows, getUserSowSigningUrl } from '../../../api/endpoints';

const ExpertProjects = () => {
  const { user } = useUser();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // One modal instance serves every card; holds the envelope being signed.
  const [signingEnvelopeId, setSigningEnvelopeId] = useState<string | null>(null);

  const fetchProjects = async ({ silent = false } = {}): Promise<any[]> => {
    try {
      if (!silent) setLoading(true);
      const response = await axiosInstant.get('/users/me/brand-projects');
      const rows = response.data?.rows || response.data || [];
      setProjects(rows);
      return rows;
    } catch (error) {
      // best-effort; the empty state covers a failed load
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // The SOW list endpoint live-checks DocuSign and repairs stale rows
  // (missed webhooks, webhook lag), so the project list that follows reads
  // reconciled state. Best-effort — a DocuSign hiccup must not block the page.
  const repairSows = async () => {
    if (!user?.userid) return;
    try {
      await axiosInstant.get(getUserSows(user.userid));
    } catch (error) {
      // best-effort; stale rows just render from the DB state
    }
  };

  useEffect(() => {
    // Show the list immediately; the repair (live DocuSign checks) only runs
    // when some card actually has a pending envelope — most visits skip the
    // extra round-trips entirely.
    (async () => {
      const rows = await fetchProjects();
      const hasPending = rows.some((p: any) => {
        const bpp = p.project_pros?.[0]?.brand_projects_pros;
        // Repair-worthy states: our own envelope awaiting signature, OR
        // signed-but-countersign-pending — the list endpoint live-checks the
        // countersign envelope for that state, which is how the "SOW Pending
        // Countersign" chip recovers when the completion webhook was missed.
        return (
          bpp?.expert_signing_status === 'sent' ||
          (bpp?.expert_signing_status === 'completed' &&
            bpp?.internal_signing_status === 'sent')
        );
      });
      if (hasPending) {
        await repairSows();
        fetchProjects({ silent: true });
      }
    })();
  }, []);

  // The SOW envelope is per selection, so every project sharing it flips to
  // signed at once. Optimistic patch first (instant chip on all sibling
  // cards), then repair-and-refetch: the repair endpoint reads DocuSign
  // directly, so the refetch can't race the webhook back to 'sent'.
  const patchSignedEnvelope = (envelopeId: string) => {
    setProjects((prev) =>
      prev.map((project) => {
        const pros = project.project_pros;
        if (pros?.[0]?.brand_projects_pros?.expert_envelope_id !== envelopeId) {
          return project;
        }
        return {
          ...project,
          project_pros: [
            {
              ...pros[0],
              brand_projects_pros: {
                ...pros[0].brand_projects_pros,
                expert_signing_status: 'completed',
              },
            },
            ...pros.slice(1),
          ],
        };
      })
    );
    repairSows().then(() => fetchProjects({ silent: true }));
  };

  return (
    <MainContainer>
      <Box className="flex flex-col gap-6">
        <PageTitle title="My Projects" />

        <ContactReminderBanner />

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <DotLottleLoader fill={false} />
          </div>
        ) : projects.length === 0 ? (
          <NoDataView
            text="No projects yet"
            subText="You'll see projects here when you've been pitched for one."
            Icon={IconVideo}
            iconProps={{ shape: 'square' }}
          />
        ) : (
          <>
            <PageDivider count={projects.length} label="Projects" />
            <div className="flex flex-col gap-3">
              {projects.map((project: any) => (
                <ExpertProjectCard
                  key={project.id}
                  project={project}
                  onSignSow={setSigningEnvelopeId}
                />
              ))}
            </div>
          </>
        )}
      </Box>

      {user?.userid && (
        <ExpertSowSignModal
          open={!!signingEnvelopeId}
          signingUrlPath={
            signingEnvelopeId ? getUserSowSigningUrl(user.userid, signingEnvelopeId) : null
          }
          onClose={() => setSigningEnvelopeId(null)}
          onSigned={() => signingEnvelopeId && patchSignedEnvelope(signingEnvelopeId)}
        />
      )}
    </MainContainer>
  );
};

export default ExpertProjects;
